import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { UploadIcon } from "lucide-react";
import { useState } from "react";
import { type SubmitHandler, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import type { z } from "zod";
import { SubmitButton } from "@/components/buttons/submit-button";
import { CustomerDataTable } from "@/components/customer-data-table";
import { NumberSelectionTable } from "@/components/number-selection-table";
import { OrderDetails } from "@/components/order-details";
import { Button, buttonVariants } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@/components/ui/input-group";
import { useCountdownTimer } from "@/hooks/use-countdown-timer";
import { useSession } from "@/hooks/use-session";
import { cn, getDeliveryOptionId } from "@/lib/utils";
import {
  useLazyReferAFriendValidationQuery,
  useSendSMSOTPMutation,
  useSubmitOrderMutation,
  useUploadCustomerFilesMutation,
  useVerifySMSOTPMutation,
} from "@/redux/services/order-portal";
import { setOrder } from "@/redux/slices/order";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { submitOrderSchema } from "@/shared/schemas/submit-order";

export const SubmitOrderForm = ({
  serviceType,
  className,
}: {
  serviceType: ServiceType;
  className?: string;
}) => {
  const { session } = useSession();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isProduction = import.meta.env.VITE_ENV === "production";

  const [serviceNumber, setServiceNumber] = useState("");

  const { minutes, seconds, isDisabled, hasStarted, resetTimer } =
    useCountdownTimer();

  const order = useAppSelector((state) => state.order);
  const dispatch = useAppDispatch();
  const [
    referAFriendValidation,
    {
      currentData: referAFriendValidationData,
      error: referAFriendValidationError,
      isSuccess: isReferAFriendValidationSuccess,
    },
  ] = useLazyReferAFriendValidationQuery();
  const [sendSMSOTP, { data: sendOTPResponse }] = useSendSMSOTPMutation();
  const [verifySMSOTP] = useVerifySMSOTPMutation();
  const [submitOrder] = useSubmitOrderMutation();
  const [uploadCustomerFiles] = useUploadCustomerFilesMutation();

  const form = useForm<z.infer<typeof submitOrderSchema>>({
    resolver: zodResolver(submitOrderSchema),
    defaultValues: {
      isAuthenticated: !!session,
      // customer_type should be non-null at this order step
      customerType: order.customerType as CustomerType,
      icFront: undefined,
      icBack: undefined,
      subscriptionForm: undefined,
      otherFile1: undefined,
      otherFile2: undefined,
      otherFile3: undefined,
      imsiNumber: session ? (order.imsiNumber ?? "") : undefined,
      otp: undefined,
      hasAgreedToTerms: false,
    },
  });

  const icFrontFile = useWatch({
    control: form.control,
    name: "icFront",
  });
  const icBackFile = useWatch({
    control: form.control,
    name: "icBack",
  });
  const subscriptionFormFile = useWatch({
    control: form.control,
    name: "subscriptionForm",
  });
  const otherFile1 = useWatch({
    control: form.control,
    name: "otherFile1",
  });
  const otherFile2 = useWatch({
    control: form.control,
    name: "otherFile2",
  });
  const otherFile3 = useWatch({
    control: form.control,
    name: "otherFile3",
  });
  const hasAgreedToTerms = useWatch({
    control: form.control,
    name: "hasAgreedToTerms",
  });

  function validateReferAFriend() {
    try {
      if (serviceNumber) {
        referAFriendValidation({
          msisdn_service_line: serviceNumber,
          // customer_type should be non-null at this order step
          customer_type: order.customerType as CustomerType,
          bundle_id: order.plan.id,
        });
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "An unknown error occurred"
      );
    }
  }

  function sendOTP() {
    try {
      if (order.mobileNumber) {
        sendSMSOTP({
          // customer_type should be non-null at this order step
          customer_type: order.customerType as CustomerType,
          phone_number: order.mobileNumber,
        });

        resetTimer();
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "An unknown error occurred"
      );
    }
  }

  const isOTPVerificationRequired = () => {
    const customerType = order.customerType;

    return isProduction
      ? customerType !== "TOURIST"
      : customerType === "RES" || customerType === "MOBI-PREPAID";
  };

  const onSubmit: SubmitHandler<z.infer<typeof submitOrderSchema>> = async (
    data
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Necessary
  ) => {
    try {
      if (isOTPVerificationRequired()) {
        if (!sendOTPResponse) {
          throw new Error("Please get a verification code first");
        }

        const verifyOTPResponse = await verifySMSOTP({
          // customer_type should be non-null at this order step
          customer_type: order.customerType as CustomerType,
          phone_number: order.mobileNumber ?? "",
          pv_code: data.otp ?? "",
          pv_expiry: sendOTPResponse.expiry,
          pv_hash: sendOTPResponse.hash,
        });
        form.setValue("otp", "");
        if (verifyOTPResponse.error) {
          throw new Error("Failed to verify verification code");
        }

        if (!verifyOTPResponse.data) {
          throw new Error("Invalid verification code");
        }
      }

      const submitOrderResponse = await submitOrder({
        // customer_type should be non-null at this order step
        type: order.customerType as CustomerType,
        rep_id: session ? session.user.organization.dealer_id : undefined,
        customer: {
          salutation: order.salutation,
          gender: order.gender,
          name: order.name,
          ic_number: order.nricNumber,
          // nricExpirationDate should be filled at this order step
          ic_expiry_date: format(
            order.nricExpirationDate as Date,
            "yyyy-MM-dd"
          ),
          date_of_arrival: order.dateOfArrival
            ? format(order.dateOfArrival, "yyyy-MM-dd")
            : undefined,
          flight_number: order.flightNumber,
          email: order.email,
          mobile_number: order.mobileNumber,
          home_number: order.homeNumber,
          office_number: order.officeNumber,
          birth_date: order.dateOfBirth
            ? format(order.dateOfBirth, "yyyy-MM-dd")
            : undefined,
          citizenship: order.citizenship,
          country_id: order.country ? Number(order.country.id) : undefined,
          country_name: order.country?.name ?? undefined,
          ...(order.billingAddress && {
            unit: order.billingAddress.unit,
            floor: order.billingAddress.floor,
            building_name: order.billingAddress.buildingName,
            house_number: order.billingAddress.houseNumber,
            block: order.billingAddress.block,
            simpang: order.billingAddress.simpang,
            street: order.billingAddress.street,
            village_id: order.billingAddress.village
              ? Number(order.billingAddress.village)
              : undefined,
            district: Number(order.billingAddress.district),
            mukim: Number(order.billingAddress.mukim),
            postal_code_id: order.billingAddress.customPostalCode
              ? undefined
              : Number(order.billingAddress.postalCode),
            postal_code: order.billingAddress.customPostalCode
              ? order.billingAddress.customPostalCode
              : undefined,
          }),
        },
        order: {
          order_status_id: 1,
          port_in: order.hasExistingServiceProvider,
          provider_name_id: order.hasExistingServiceProvider
            ? order.existingServiceProvider?.id
            : undefined,
          provider_number: order.hasExistingServiceProvider
            ? order.msisdnNumber
            : undefined,
          bundles: order.bundles,
          preferred_number_id: order.msisdnId,
          preferred_number: order.msisdnNumber,
          imsi_number: data.imsiNumber ?? order.imsiNumber,
          delivery_option_id: order.fulfilmentType
            ? getDeliveryOptionId({
                // customer_type should be non-null at this order step
                customerType: order.customerType as CustomerType,
                fulfilmentType: order.fulfilmentType,
              })
            : undefined,
          delivery_date: order.fulfilmentDate
            ? format(order.fulfilmentDate, "yyyy-MM-dd")
            : undefined,
          address:
            order.customerType === "RES" && order.deliveryAddress
              ? {
                  unit: order.deliveryAddress.unit,
                  floor: order.deliveryAddress.floor,
                  building_name: order.deliveryAddress.buildingName,
                  house_number: order.deliveryAddress.houseNumber,
                  block: order.deliveryAddress.block,
                  simpang: order.deliveryAddress.simpang,
                  street: order.deliveryAddress.street,
                  village_id: Number(order.deliveryAddress.village),
                  district: Number(order.deliveryAddress.district),
                  mukim: Number(order.deliveryAddress.mukim),
                  postal_code_id: order.deliveryAddress.customPostalCode
                    ? undefined
                    : Number(order.deliveryAddress.postalCode),
                  postal_code: order.deliveryAddress.customPostalCode
                    ? order.deliveryAddress.customPostalCode
                    : undefined,
                }
              : undefined,
          delivery_address:
            (order.fulfilmentType === "Instant Delivery" ||
              order.fulfilmentType === "Delivery") &&
            order.deliveryAddress
              ? {
                  unit: order.deliveryAddress.unit,
                  floor: order.deliveryAddress.floor,
                  building_name: order.deliveryAddress.buildingName,
                  house_number: order.deliveryAddress.houseNumber,
                  block: order.deliveryAddress.block,
                  simpang: order.deliveryAddress.simpang,
                  street: order.deliveryAddress.street,
                  village_id: Number(order.deliveryAddress.village),
                  district: Number(order.deliveryAddress.district),
                  mukim: Number(order.deliveryAddress.mukim),
                  postal_code_id: order.deliveryAddress.customPostalCode
                    ? undefined
                    : Number(order.deliveryAddress.postalCode),
                  postal_code: order.deliveryAddress.customPostalCode
                    ? order.deliveryAddress.customPostalCode
                    : undefined,
                }
              : undefined,
          contract_id: order.contractId ? order.contractId : undefined,
          booked_id: order.bookedId,
          pickup_address_id:
            order.fulfilmentType === "Pick Up" && order.store
              ? Number(order.store.id)
              : undefined,
          ...(order.customerType !== "TOURIST" && {
            referral: isReferAFriendValidationSuccess
              ? referAFriendValidationData
              : undefined,
          }),
        },
      });
      if (submitOrderResponse.error) {
        throw new Error("Failed to submit order");
      }

      if (order.customerType !== "TOURIST") {
        const formData = new FormData();
        // customer_type should be non-null at this order step
        formData.append("customer_type", order.customerType as CustomerType);
        if (
          submitOrderResponse.data.customer_type === "MOBI-POSTPAID" ||
          submitOrderResponse.data.customer_type === "FREEDOM"
        ) {
          formData.append("order_id", submitOrderResponse.data.order_id);
          formData.append(
            "subscription_id",
            submitOrderResponse.data.subscription_id
          );
        }
        formData.append(
          "customer_id",
          submitOrderResponse.data.customer_id.toString()
        );
        if (icFrontFile) {
          formData.append("attachments", icFrontFile);
        }
        if (icBackFile) {
          formData.append("attachments", icBackFile);
        }
        if (subscriptionFormFile) {
          formData.append("attachments", subscriptionFormFile);
        }
        if (otherFile1) {
          formData.append("attachments", otherFile1);
        }
        if (otherFile2) {
          formData.append("attachments", otherFile2);
        }
        if (otherFile3) {
          formData.append("attachments", otherFile3);
        }

        const uploadCustomerFilesResponse = await uploadCustomerFiles(formData);
        if (uploadCustomerFilesResponse.error) {
          throw new Error("Failed to upload customer files");
        }
      }

      const updatedOrder = {
        ...order,
        customerId: submitOrderResponse.data.customer_id,
        orderId:
          submitOrderResponse.data.customer_type === "MOBI-POSTPAID" ||
          submitOrderResponse.data.customer_type === "FREEDOM"
            ? submitOrderResponse.data.order_id
            : (submitOrderResponse.data as SubmitOrderCRMResponse).id,
        subscriptionId:
          submitOrderResponse.data.customer_type === "MOBI-POSTPAID" ||
          submitOrderResponse.data.customer_type === "FREEDOM"
            ? submitOrderResponse.data.subscription_id
            : undefined,
      };

      dispatch(setOrder(updatedOrder));

      navigate(`/${serviceType}/checkout`);

      form.reset();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "An unknown error occurred"
      );
    }
  };

  return (
    <Form {...form}>
      <form
        className={cn("border", className)}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className="flex flex-col lg:flex-row">
          <section className="space-y-6 border p-6 lg:basis-2/3">
            <div className="space-y-6">
              <p className="font-semibold text-lg uppercase">
                {t("summary.titles.customer-data")}
              </p>
              <CustomerDataTable order={order} serviceType={serviceType} />
            </div>
            <div className="flex flex-col gap-6 lg:flex-row">
              <div className="space-y-2 lg:basis-1/2">
                <p className="font-semibold text-lg uppercase">
                  {serviceType === "infinity"
                    ? t("summary.titles.service-address")
                    : // biome-ignore lint/style/noNestedTernary: Necessary
                      serviceType === "tourist"
                      ? t("summary.titles.pick-up-address")
                      : t("summary.titles.delivery")}
                </p>
                {serviceType !== "infinity" && serviceType !== "tourist" && (
                  <>
                    <p>
                      {t("labels.type")}:{" "}
                      <span className="uppercase">{order.fulfilmentType}</span>
                    </p>
                    <p>
                      {t("labels.date")}:{" "}
                      {order.fulfilmentDate &&
                        format(order.fulfilmentDate, "dd MMMM yyyy")}
                    </p>
                    {order.store && (
                      <p>
                        {t("labels.store")}: {order.store.name}
                      </p>
                    )}
                  </>
                )}
                {serviceType === "tourist"
                  ? order.store && <p>{order.store.name}</p>
                  : order.deliveryAddress && (
                      <p>{order.deliveryAddress.address}</p>
                    )}
              </div>
              {serviceType !== "tourist" && (
                <div className="space-y-2 lg:basis-1/2">
                  <p className="font-semibold text-lg uppercase">
                    {t("summary.titles.billing-address")}
                  </p>
                  <p>{order.billingAddress?.address}</p>
                </div>
              )}
            </div>
            {serviceType !== "tourist" && (
              <div className="space-y-4">
                <p className="font-semibold text-lg uppercase">
                  {t("summary.titles.upload-file")}
                </p>
                <div className="space-y-2">
                  <FormField
                    control={form.control}
                    name="icFront"
                    render={({ field: { name, ref, onBlur, onChange } }) => (
                      <FormItem>
                        <div className="flex flex-row items-center gap-4">
                          <FormLabel
                            className={cn(
                              buttonVariants({ variant: "outline" }),
                              "border-primary text-primary"
                            )}
                          >
                            <UploadIcon />
                            <span>{t("labels.ic-front")} *</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              className="hidden"
                              name={name}
                              onBlur={onBlur}
                              onChange={(event) =>
                                onChange(event.target.files?.[0] || null)
                              }
                              ref={ref}
                              type="file"
                            />
                          </FormControl>
                          {icFrontFile && (
                            <span className="text-sm">{icFrontFile.name}</span>
                          )}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="icBack"
                    render={({ field: { name, ref, onBlur, onChange } }) => (
                      <FormItem>
                        <div className="flex flex-row items-center gap-4">
                          <FormLabel
                            className={cn(
                              buttonVariants({ variant: "outline" }),
                              "border-primary text-primary"
                            )}
                          >
                            <UploadIcon />
                            <span>{t("labels.ic-back")} *</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              className="hidden"
                              name={name}
                              onBlur={onBlur}
                              onChange={(event) =>
                                onChange(event.target.files?.[0] || null)
                              }
                              ref={ref}
                              type="file"
                            />
                          </FormControl>
                          {icBackFile && (
                            <span className="text-sm">{icBackFile.name}</span>
                          )}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {session && (
                    <>
                      <FormField
                        control={form.control}
                        name="subscriptionForm"
                        render={({
                          field: { name, ref, onBlur, onChange },
                        }) => (
                          <FormItem>
                            <div className="flex flex-row items-center gap-4">
                              <FormLabel
                                className={cn(
                                  buttonVariants({ variant: "outline" }),
                                  "border-primary text-primary"
                                )}
                              >
                                <UploadIcon />
                                <span>{t("labels.subscription-form")} *</span>
                              </FormLabel>
                              <FormControl>
                                <Input
                                  className="hidden"
                                  name={name}
                                  onBlur={onBlur}
                                  onChange={(event) =>
                                    onChange(event.target.files?.[0] || null)
                                  }
                                  ref={ref}
                                  type="file"
                                />
                              </FormControl>
                              {subscriptionFormFile && (
                                <span className="text-sm">
                                  {subscriptionFormFile.name}
                                </span>
                              )}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="otherFile1"
                        render={({
                          field: { name, ref, onBlur, onChange },
                        }) => (
                          <FormItem>
                            <div className="flex flex-row items-center gap-4">
                              <FormLabel
                                className={cn(
                                  buttonVariants({ variant: "outline" }),
                                  "border-primary text-primary"
                                )}
                              >
                                <UploadIcon />
                                <span>{t("labels.other-file-1")}</span>
                              </FormLabel>
                              <FormControl>
                                <Input
                                  className="hidden"
                                  name={name}
                                  onBlur={onBlur}
                                  onChange={(event) =>
                                    onChange(event.target.files?.[0] || null)
                                  }
                                  ref={ref}
                                  type="file"
                                />
                              </FormControl>
                              {otherFile1 && (
                                <span className="text-sm">
                                  {otherFile1.name}
                                </span>
                              )}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="otherFile2"
                        render={({
                          field: { name, ref, onBlur, onChange },
                        }) => (
                          <FormItem>
                            <div className="flex flex-row items-center gap-4">
                              <FormLabel
                                className={cn(
                                  buttonVariants({ variant: "outline" }),
                                  "border-primary text-primary"
                                )}
                              >
                                <UploadIcon />
                                <span>{t("labels.other-file-2")}</span>
                              </FormLabel>
                              <FormControl>
                                <Input
                                  className="hidden"
                                  name={name}
                                  onBlur={onBlur}
                                  onChange={(event) =>
                                    onChange(event.target.files?.[0] || null)
                                  }
                                  ref={ref}
                                  type="file"
                                />
                              </FormControl>
                              {otherFile2 && (
                                <span className="text-sm">
                                  {otherFile2.name}
                                </span>
                              )}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="otherFile3"
                        render={({
                          field: { name, ref, onBlur, onChange },
                        }) => (
                          <FormItem>
                            <div className="flex flex-row items-center gap-4">
                              <FormLabel
                                className={cn(
                                  buttonVariants({ variant: "outline" }),
                                  "border-primary text-primary"
                                )}
                              >
                                <UploadIcon />
                                <span>{t("labels.other-file-3")}</span>
                              </FormLabel>
                              <FormControl>
                                <Input
                                  className="hidden"
                                  name={name}
                                  onBlur={onBlur}
                                  onChange={(event) =>
                                    onChange(event.target.files?.[0] || null)
                                  }
                                  ref={ref}
                                  type="file"
                                />
                              </FormControl>
                              {otherFile3 && (
                                <span className="text-sm">
                                  {otherFile3.name}
                                </span>
                              )}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </>
                  )}
                </div>
                <p className="text-xs">{t("summary.descriptions.file")}</p>
              </div>
            )}
          </section>
          <section className="divide-y border lg:basis-1/3">
            <div className="space-y-6 p-6">
              <p className="font-semibold text-lg uppercase">
                {t("summary.titles.package-summary")}
              </p>
              <OrderDetails order={order} serviceType={serviceType} />
            </div>
            {serviceType !== "easi" && serviceType !== "tourist" && (
              <div className="space-y-6 p-6">
                <p className="font-semibold text-lg uppercase">
                  {t("summary.titles.refer-a-friend")}
                </p>
                <div className="space-y-2">
                  <ButtonGroup className="w-full">
                    <Input
                      disabled={isReferAFriendValidationSuccess}
                      maxLength={7}
                      onChange={(event) => setServiceNumber(event.target.value)}
                      placeholder={t("labels.refer-a-friend")}
                      value={serviceNumber}
                    />
                    <Button
                      disabled={isReferAFriendValidationSuccess}
                      onClick={validateReferAFriend}
                      type="button"
                    >
                      {t("labels.validate")}
                    </Button>
                  </ButtonGroup>
                  {isReferAFriendValidationSuccess &&
                    referAFriendValidationData && (
                      <p className="text-green-500">
                        {referAFriendValidationData.reward?.display_message}
                      </p>
                    )}
                  {referAFriendValidationError && (
                    <p className="text-red-500">
                      {
                        (referAFriendValidationError as APIErrorResponse)?.data
                          .message
                      }
                    </p>
                  )}
                </div>
              </div>
            )}
          </section>
        </div>

        {serviceType !== "infinity" && serviceType !== "tourist" && (
          <section className="space-y-6 border p-6 lg:px-24 lg:py-12">
            <p className="font-semibold text-lg uppercase">
              {t("summary.titles.your-number-selection")}
            </p>

            <NumberSelectionTable form={form} order={order} />
          </section>
        )}

        <div className="flex flex-col lg:flex-row">
          {serviceType !== "tourist" && (
            <section className="space-y-6 border bg-muted p-6 text-center lg:basis-1/2">
              <p>{t("summary.descriptions.verification-code")}</p>
              <FormField
                control={form.control}
                name="otp"
                render={({ field }) => (
                  <FormItem className="mx-auto flex max-w-md flex-col items-center">
                    <FormLabel className="text-center">
                      {t("labels.verification-code")}
                    </FormLabel>
                    <ButtonGroup>
                      <Button
                        disabled={isDisabled}
                        onClick={sendOTP}
                        type="button"
                      >
                        {t("labels.get-code")}
                      </Button>
                      <InputGroup>
                        {sendOTPResponse && (
                          <InputGroupAddon>
                            <InputGroupText>
                              {sendOTPResponse.prefix}
                            </InputGroupText>
                          </InputGroupAddon>
                        )}
                        <FormControl>
                          <InputGroupInput
                            disabled={!sendOTPResponse}
                            maxLength={6}
                            placeholder={sendOTPResponse && "Enter Code"}
                            {...field}
                          />
                        </FormControl>
                        {hasStarted && (
                          <p className="absolute -right-12 text-sm">
                            {minutes}:{String(seconds).padStart(2, "0")}
                          </p>
                        )}
                      </InputGroup>
                    </ButtonGroup>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </section>
          )}
          <section
            className={cn(
              "flex flex-col items-center space-y-6 border p-6",
              serviceType === "tourist" ? "lg:basis-full" : "lg:basis-1/2"
            )}
          >
            <FormField
              control={form.control}
              name="hasAgreedToTerms"
              render={({ field }) => (
                <FormItem className="max-w-sm">
                  <div className="flex flex-row items-start gap-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="flex flex-wrap items-baseline gap-x-1">
                        <span>{t("labels.acknowledge-1")}</span>
                        <a
                          className="text-primary underline hover:text-primary/90"
                          href="https://dst.com.bn/legal/"
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          {t("labels.terms-and-conditions")}
                        </a>
                        <span>{t("labels.and")}</span>
                        <a
                          className="text-primary underline hover:text-primary/90"
                          href="https://dst.com.bn/legal/"
                          rel="noopener noreferrer"
                          target="_blank"
                        >
                          {t("labels.privacy-policy")}
                        </a>
                        <span>{t("labels.acknowledge-2")}</span>
                      </FormLabel>
                    </div>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <SubmitButton
              className="rounded-md"
              disabled={!hasAgreedToTerms}
              isSubmitting={form.formState.isSubmitting}
            >
              {t("labels.submit")}
            </SubmitButton>
          </section>
        </div>
      </form>
    </Form>
  );
};
